/**
 *
 * @module eventSchema
 *
 */

import Check from 'check-types';
import EventValue from './eventValue';
import Services from './../services/entry';

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    object,
    nonEmptyString,
    instanceStrict
} = Check.assert;

/**
 *
 * @class EventSchema
 * @desc An event payload schema that can be customized by the application developer
 *
 */
export default class EventSchema {

    /**
     *
     * @param {Object<String, SDK.UserActivity.EventValue>} [values={}]
     *
     */
    constructor(values = {}) {

        const errorMessage = {
            ctor: 'EventSchema(values)',
            get values() {
                return `${this.ctor} values should be an Object of <String, SDK.UserActivity.EventValue>`;
            }
        };

        if (Check.assigned(values)) {

            object(values, errorMessage.values, InvalidArgumentException);

            Check.assert(
                Check.all(Object.values(values).map((value) => {
                    return instanceStrict(value, EventValue);
                })),
                errorMessage.values,
                InvalidArgumentException
            );

        }

        /**
         *
         * @private
         * @type {Object<String, SDK.UserActivity.EventValue>|Object}
         *
         */
        this.values = values;

    }

    /**
     *
     * @access public
     * @param {String} name
     * @param {SDK.UserActivity.EventValue} value
     *
     */
    setValue(name, value) {

        const errorMessage = {
            method: 'EventSchema.setValue(name, value)',
            get name() {
                return `${this.method} name should be a String with length`;
            },
            get value() {
                return `${this.method} value should be an instance of SDK.UserActivity.EventValue`;
            }
        };

        nonEmptyString(name, errorMessage.name, InvalidArgumentException);
        instanceStrict(value, EventValue, errorMessage.value, InvalidArgumentException);

        this.values[name] = value;

    }

    /**
     *
     * @access public
     * @param {String} name
     *
     */
    removeValue(name) {

        const errorMessage = {
            method: 'EventSchema.removeValue(name)',
            get name() {
                return `${this.method} name should be a String with length`;
            }
        };

        nonEmptyString(name, errorMessage.name, InvalidArgumentException);

        delete this.values[name];

    }

    /**
     *
     * @access private
     * @param {Object} trackingParameters
     * @desc loops through all of the schemas values and generates a new value
     * @note the JS SDK passes in `trackingParameters` which is a reference to the combined global tracking parameters
     * and what the application developer passed into `UserActivityApi.trackEvent(event, trackingParameters)`
     * it's possible that `generateValue` ignores `trackingParameters` this depends on how the application developer
     * structured this callback when creating a new instance of `EventSchema` - if nothing was provided we ignore it
     * and return the result of this method
     * @note we wrap the function call in a try/catch to protect against possible errors when the application developer
     * provides the callback invoked by `.generateValue()` - the failed keys are added to an array and reported
     * in `UserActivityApi.trackEvent` and are stripped from the payload before sending to `Dust` - not part of the spec
     * @returns {Object}
     *
     */
    generateEventData(trackingParameters) {

        const errorMessage = {
            method: 'EventSchema.generateEventData(trackingParameters)',
            get trackingParameters() {
                return `${this.method} trackingParameters should be an Object`;
            }
        };

        object(trackingParameters, errorMessage.trackingParameters, InvalidArgumentException);

        const { values } = this;

        const eventData = {};

        for (const item of (Object.keys(values))) {

            try {
                eventData[item] = values[item].generateValue(trackingParameters);
            } catch (ex) {
                eventData._failures = Array.isArray(eventData._failures) ?
                    eventData._failures.push(ex.stack) :
                    eventData._failures = [ex.stack];
            }

        }

        return eventData;

    }

}



// WEBPACK FOOTER //
// ./src/userActivity/eventSchema.js